import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Calendar, Clock, MapPin, Building2, User, FileText,
  CheckCircle2, AlertTriangle, ShieldCheck, Loader2, X
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_MAP = {
  conforme: { label: "Conforme", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  conforme_observaciones: { label: "Conforme con observaciones", color: "bg-amber-100 text-amber-800 border-amber-300", icon: AlertTriangle },
  requiere_acciones: { label: "Requiere acciones correctivas", color: "bg-red-100 text-red-800 border-red-300", icon: AlertTriangle },
};

function resolveImg(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

function PhotoGallery({ photos, label }) {
  const [active, setActive] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <button
            key={i}
            onClick={() => setActive(p)}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-emerald-500 transition"
            data-testid={`public-photo-${label}-${i}`}
          >
            <img
              src={resolveImg(p.url)}
              alt={`${label} ${i + 1}`}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="public-photo-modal"
        >
          <button
            onClick={() => setActive(null)}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={resolveImg(active.url)}
            alt="Vista ampliada"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">{label}</p>
        <p className="text-sm text-gray-900 break-words">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="border-t border-gray-100 pt-5 mt-5">
      <h2 className="text-sm font-bold uppercase tracking-wide text-emerald-800 mb-3">{title}</h2>
      {children}
    </section>
  );
}

function DocItem({ doc, photos }) {
  const docPhotos = photos || [];
  return (
    <li className="flex flex-col gap-2 py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-2">
        <FileText className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
        <span className="text-sm text-gray-800 flex-1">{doc}</span>
      </div>
      {docPhotos.length > 0 && (
        <div className="ml-6">
          <PhotoGallery photos={docPhotos} label="doc" />
        </div>
      )}
    </li>
  );
}

export default function ActaPublica() {
  const { id } = useParams();
  const [acta, setActa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActa = async () => {
      try {
        const res = await axios.get(`${API}/public/acta/${id}`);
        setActa(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Acta no encontrada" : "Error al cargar el acta");
      } finally {
        setLoading(false);
      }
    };
    fetchActa();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-emerald-700 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center max-w-md w-full" data-testid="public-acta-error">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-2">No se pudo cargar el acta</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const estadoInfo = ESTADO_MAP[acta.estado_finca] || { label: acta.estado_finca, color: "bg-gray-100 text-gray-800 border-gray-300", icon: ShieldCheck };
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-gray-50 to-white" data-testid="public-acta-view">
      {/* Header */}
      <header className="bg-emerald-800 text-white shadow">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-emerald-100">Vista pública verificada</p>
                <h1 className="text-base sm:text-lg font-bold">Acta de Visita Técnica</h1>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase text-emerald-100">N°</p>
              <p className="text-base sm:text-lg font-mono font-bold">{acta.numero}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content card */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 sm:p-7">
          {/* Estado finca prominente */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold mb-5 ${estadoInfo.color}`} data-testid="public-acta-estado">
            <EstadoIcon className="w-3.5 h-3.5" />
            {estadoInfo.label}
          </div>

          {/* Info general */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoItem icon={Calendar} label="Fecha" value={acta.fecha} />
            <InfoItem icon={Clock} label="Horario" value={acta.hora_inicio || acta.hora_fin ? `${acta.hora_inicio || "—"} a ${acta.hora_fin || "—"}` : null} />
            <InfoItem icon={MapPin} label="Ubicación" value={acta.ubicacion} />
            <InfoItem icon={Building2} label="Finca" value={acta.finca} />
            <InfoItem icon={User} label="Representante" value={acta.representante} />
            <InfoItem icon={User} label="Técnico visitante" value={acta.tecnico ? `${acta.tecnico}${acta.empresa ? ` (${acta.empresa})` : ""}` : null} />
          </div>

          {/* Objetivos */}
          {(acta.objetivos?.length > 0 || acta.objetivo_otro) && (
            <Section title="Objetivos de la visita">
              <div className="flex flex-wrap gap-2">
                {acta.objetivos?.map((o, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 text-xs font-medium border border-emerald-200">{o}</span>
                ))}
                {acta.objetivo_otro && (
                  <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 text-xs font-medium border border-emerald-200">{acta.objetivo_otro}</span>
                )}
              </div>
            </Section>
          )}

          {/* Documentos entregados */}
          {acta.documentos_entregados?.length > 0 && (
            <Section title="Documentos entregados por la finca">
              <ul className="space-y-1">
                {acta.documentos_entregados.map((d, i) => (
                  <DocItem key={i} doc={d} photos={acta.fotos_documentos_entregados?.[String(i)]} />
                ))}
              </ul>
            </Section>
          )}

          {/* Documentos recibidos */}
          {acta.documentos_recibidos?.length > 0 && (
            <Section title="Documentos recibidos">
              <ul className="space-y-1">
                {acta.documentos_recibidos.map((d, i) => (
                  <DocItem key={i} doc={d} photos={acta.fotos_documentos_recibidos?.[String(i)]} />
                ))}
              </ul>
            </Section>
          )}

          {/* Observaciones */}
          {(acta.pendientes || acta.observaciones) && (
            <Section title="Observaciones">
              {acta.pendientes && (
                <p className="text-sm text-gray-800 mb-2">
                  <strong className="text-gray-900">Pendientes:</strong> {acta.pendientes}
                </p>
              )}
              {acta.observaciones && (
                <p className="text-sm text-gray-800">
                  <strong className="text-gray-900">Generales:</strong> {acta.observaciones}
                </p>
              )}
              {acta.fotos_observaciones?.length > 0 && (
                <div className="mt-3">
                  <PhotoGallery photos={acta.fotos_observaciones} label="obs" />
                </div>
              )}
            </Section>
          )}

          {/* Inspección */}
          {(acta.inspeccion?.length > 0 || acta.hallazgos || acta.fotos_hallazgos?.length > 0) && (
            <Section title="Inspección de campo">
              {acta.inspeccion?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {acta.inspeccion.map((item, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">{item}</span>
                  ))}
                  {acta.inspeccion_otro && (
                    <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">{acta.inspeccion_otro}</span>
                  )}
                </div>
              )}
              {acta.hallazgos && (
                <p className="text-sm text-gray-800 mb-3">
                  <strong className="text-gray-900">Hallazgos:</strong> {acta.hallazgos}
                </p>
              )}
              {acta.fotos_hallazgos?.length > 0 && <PhotoGallery photos={acta.fotos_hallazgos} label="hallazgos" />}
            </Section>
          )}

          {/* Estado finca + incumplimientos */}
          <Section title="Estado de la finca">
            <div className="text-sm text-gray-800 space-y-1">
              <p><strong className="text-gray-900">Resultado:</strong> {estadoInfo.label}</p>
              <p>
                <strong className="text-gray-900">Incumplimientos críticos:</strong>{" "}
                {acta.incumplimientos_criticos
                  ? <span className="text-red-700 font-medium">SE DETECTARON incumplimientos críticos</span>
                  : <span className="text-emerald-700 font-medium">No se detectaron</span>}
              </p>
            </div>
          </Section>

          {/* Acciones correctivas */}
          {acta.acciones_correctivas?.length > 0 && (
            <Section title="Acciones correctivas">
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-emerald-800 text-white">
                      <th className="text-left px-3 py-2 text-xs font-semibold w-8">#</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold">Acción</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold">Responsable</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold">Compromiso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acta.acciones_correctivas.map((a, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-3 py-2 text-gray-600">{i + 1}</td>
                        <td className="px-3 py-2 text-gray-800">{a.accion}</td>
                        <td className="px-3 py-2 text-gray-800">{a.responsable}</td>
                        <td className="px-3 py-2 text-gray-800">{a.fecha_compromiso}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}

          {/* Foto del acta física */}
          {acta.fotos_acta_fisica?.length > 0 && (
            <Section title="Foto del acta física">
              <PhotoGallery photos={acta.fotos_acta_fisica} label="acta-fisica" />
            </Section>
          )}

          {/* Firmas info (sin mostrar firma real) */}
          <Section title="Firmas">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Este acta fue firmada digitalmente por el representante de la finca y el técnico visitante. Las firmas se mantienen en custodia
                en el documento PDF original. Esta vista pública omite las firmas para proteger su integridad.
              </p>
            </div>
          </Section>

          {/* Trazabilidad */}
          <div className="border-t border-gray-200 pt-4 mt-6 text-[11px] text-gray-500 space-y-0.5">
            <p>Creado por: <span className="text-gray-700 font-medium">{acta.created_by || "—"}</span></p>
            <p>Fecha de creación: <span className="text-gray-700 font-medium">{acta.created_at?.slice(0, 10) || "—"}</span></p>
            <p>Última modificación: <span className="text-gray-700 font-medium">{acta.updated_at?.slice(0, 10) || "—"}</span></p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-gray-500">
          <p>Documento generado por <strong className="text-emerald-800">Ciro del Pacífico</strong></p>
          <p className="mt-1">Sistema de Gestión Agrícola — Vista pública oficial</p>
        </div>
      </main>
    </div>
  );
}
